import React from 'react';
import { ArrowRight, QrCode, Upload, CreditCard, Printer } from 'lucide-react';
import { Button } from './ui/button';
import { kiosks } from '../data/mockData';

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickSteps = [
    { icon: QrCode, text: 'Scan QR' },
    { icon: Upload, text: 'Upload' },
    { icon: CreditCard, text: 'Pay' },
    { icon: Printer, text: 'Print' }
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 via-white to-gray-50"></div>
      <div className="absolute top-20 right-0 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-100/40 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fadeIn">
            <div className="inline-block">
              <span className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold">
                Smart Printing Solution
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              Print Smarter.
              <br />
              <span className="text-yellow-500">Faster.</span>
              <br />
              Anywhere.
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
              PRINTLEY brings self-service smart printing kiosks to colleges, offices, and public spaces. 
              Scan, upload, pay, and print – all in seconds.
            </p>

            {/* Quick Steps */}
            <div className="flex flex-wrap gap-4 pt-4">
              {quickSteps.map((step, index) => (
                <div key={index} className="flex items-center gap-2 bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-100">
                  <step.icon className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">{step.text}</span>
                  {index < quickSteps.length - 1 && (
                    <ArrowRight className="w-4 h-4 text-gray-400 ml-2" />
                  )}
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Button 
                onClick={() => scrollToSection('#how-it-works')}
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
              >
                How It Works
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => scrollToSection('#contact')}
                variant="outline"
                className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-200"
              >
                Get a Kiosk
              </Button>
            </div>
          </div>

          {/* Right Content - Kiosk Image */}
          <div className="relative lg:h-[600px] flex items-center justify-center">
            <div className="relative animate-float">
              <img 
                src={kiosks.images[0]} 
                alt="PRINTLEY Smart Kiosk" 
                className="w-full max-w-lg h-auto object-contain drop-shadow-2xl"
              />
              {/* Floating Stats */}
              <div className="absolute -top-8 -left-8 bg-white rounded-2xl shadow-xl p-4 animate-fadeIn">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Printer className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">15K+</p>
                    <p className="text-sm text-gray-600">Prints Daily</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-xl p-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <QrCode className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">3 Sec</p>
                    <p className="text-sm text-gray-600">Avg. Speed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
